import { Handle, Position } from 'reactflow';
import styles from '../styles/nodes.module.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGear, faX, faCalculator, faCodeCompare, faTriangleExclamation, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {useEffect, useState} from "react";
import { isComparisonOperator, isArithmeticOperator } from "../utils/operationValidation";
import { useTranslation } from 'react-i18next';

export default function OperationNode({ id, data }) {
    const { t } = useTranslation();
    const [operatorType, setOperatorType] = useState(data.operation)
    const [dataField1, setDataField1] = useState(data.value_1);
    const [dataField2, setDataField2] = useState(data.value_2);
    const [resultName, setResultName] = useState(data.result_name || '');
    const [mapFromLow, setMapFromLow] = useState(data.map_from_low ?? 4);
    const [mapFromHigh, setMapFromHigh] = useState(data.map_from_high ?? 20);
    const [mapToLow, setMapToLow] = useState(data.map_to_low ?? 0);
    const [mapToHigh, setMapToHigh] = useState(data.map_to_high ?? 100);

    useEffect(() => {
        setOperatorType(data.operation);
        setDataField1(data.value_1);
        setDataField2(data.value_2);
        setResultName(data.result_name || '');
        setMapFromLow(data.map_from_low ?? 4);
        setMapFromHigh(data.map_from_high ?? 20);
        setMapToLow(data.map_to_low ?? 0);
        setMapToHigh(data.map_to_high ?? 100);
    }, [data]);

    const handleOperatorChange = (event) => {
        const newOperatorType = event.target.value;
        setOperatorType(newOperatorType);
        data.operation = newOperatorType;
        if (newOperatorType === '18') {
            if (data.map_from_low == null) { data.map_from_low = 4; setMapFromLow(4); }
            if (data.map_from_high == null) { data.map_from_high = 20; setMapFromHigh(20); }
            if (data.map_to_low == null) { data.map_to_low = 0; setMapToLow(0); }
            if (data.map_to_high == null) { data.map_to_high = 100; setMapToHigh(100); }
        }
    }

    const handleMapParamChange = (setter, key) => (event) => {
        const val = event.target.value;
        setter(val);
        data[key] = val;
    };

    const handleDataChange = (event, index) => {
        const newData = event.target.value;
        if(index === 1) {
            setDataField1(newData);
            data.value_1 = newData;
        }
        else if(index === 2) {
            setDataField2(newData);
            data.value_2 = newData;
        }
    }

    const handleBoolChange = (event, index) => {
        const val = event.target.value;
        if(index === 1) {
            setDataField1(val);
            data.value_1 = val;
        } else if(index === 2) {
            setDataField2(val);
            data.value_2 = val;
        }
    }

    const handleResultNameChange = (event) => {
        const newName = event.target.value;
        setResultName(newName);
        data.result_name = newName;
    }

    // Check if this is an arithmetic or comparison operator
    const isArithmetic = isArithmeticOperator(operatorType);
    const isComparison = isComparisonOperator(operatorType);

    // Determine allowed operators based on connected input types
    const getConnectedTypes = () => {
        const type1 = data.is_connected_1 ? (data.connected_type_1 || 'number') : null;
        const type2 = data.is_connected_2 ? (data.connected_type_2 || 'number') : null;
        return { type1, type2 };
    };

    const { type1: connType1, type2: connType2 } = getConnectedTypes();

    // Check type compatibility and get allowed operators
    const getOperatorFilter = () => {
        const oneConnected = (connType1 && !connType2) || (!connType1 && connType2);
        const bothConnected = connType1 && connType2;

        // No inputs connected — show all
        if (!connType1 && !connType2) return { allowed: null, incompatible: false };

        // Both connected with different types — incompatible
        if (bothConnected && connType1 !== connType2) {
            return { allowed: [], incompatible: true };
        }

        // Use whichever type is known
        const effectiveType = connType1 || connType2;

        let ops;
        switch (effectiveType) {
            case 'boolean':
                ops = ['9', '10']; // =, ≠
                break;
            case 'string':
                ops = ['9', '10']; // =, ≠
                break;
            case 'number':
            default:
                ops = ['1','2','3','4','5','6','7','8','9','10'];
                break;
        }

        // NOT only for boolean inputs (logical negation)
        if (oneConnected && effectiveType === 'boolean') {
            ops = [...ops, '16'];
        }
        // Delta and Map only for numbers
        if (oneConnected && (effectiveType === 'number' || effectiveType === undefined)) {
            ops = [...ops, '17', '18'];
        }

        return { allowed: ops, incompatible: false };
    };

    const { allowed: allowedOperators, incompatible: typesIncompatible } = getOperatorFilter();

    // All possible operators with their labels
    const ALL_OPERATORS = [
        { value: '1', label: t('operations.operators.add') },
        { value: '2', label: t('operations.operators.subtract') },
        { value: '3', label: t('operations.operators.multiply') },
        { value: '4', label: t('operations.operators.divide') },
        { value: '5', label: t('operations.operators.lessThan') },
        { value: '6', label: t('operations.operators.greaterThan') },
        { value: '7', label: t('operations.operators.lessOrEqual') },
        { value: '8', label: t('operations.operators.greaterOrEqual') },
        { value: '9', label: t('operations.operators.equal') },
        { value: '10', label: t('operations.operators.notEqual') },
        { value: '16', label: t('operations.operators.not') },
        { value: '17', label: t('operations.operators.delta') },
        { value: '18', label: t('operations.operators.map') },
    ];

    const filteredOperators = allowedOperators === null
        ? ALL_OPERATORS
        : ALL_OPERATORS.filter(op => allowedOperators.includes(op.value));

    // Determine the effective type for each input based on the OTHER connected input's type
    // If input 1 is connected to a boolean variable, input 2 should show True/False selector
    const getInputType = (inputIndex) => {
        const otherConnected = inputIndex === 1 ? data.is_connected_2 : data.is_connected_1;
        const otherType = inputIndex === 1 ? data.connected_type_2 : data.connected_type_1;
        const selfConnected = inputIndex === 1 ? data.is_connected_1 : data.is_connected_2;
        const selfType = inputIndex === 1 ? data.connected_type_1 : data.connected_type_2;

        // If this input itself is connected, it shows a label (no input needed)
        if (selfConnected) return null;

        // Use the other input's type to determine what kind of input to show
        if (otherConnected && otherType) return otherType;

        // If self has a type set (from previous connection), use it
        if (selfType) return selfType;

        return 'number';
    };

    const renderInput = (inputIndex) => {
        const isConnected = inputIndex === 1 ? data.is_connected_1 : data.is_connected_2;
        const value = inputIndex === 1 ? data.value_1 : data.value_2;
        const fieldValue = inputIndex === 1 ? dataField1 : dataField2;

        if (isConnected) {
            return <div className={styles.scratch_label}>{value}</div>;
        }

        const inputType = getInputType(inputIndex);

        if (inputType === 'boolean') {
            return (
                <select
                    className={`${styles.scratch_select} nodrag my-2`}
                    onChange={(event) => handleBoolChange(event, inputIndex)}
                    value={parseFloat(fieldValue) === 1 || fieldValue === '1' ? '1' : '0'}
                >
                    <option value={'1'}>{t('operations.nodes.boolTrue')}</option>
                    <option value={'0'}>{t('operations.nodes.boolFalse')}</option>
                </select>
            );
        }

        if (inputType === 'string') {
            return (
                <input
                    className={`${styles.scratch_input} nodrag my-2`}
                    type="text"
                    placeholder={t('operations.nodes.textValue')}
                    onChange={(event) => handleDataChange(event, inputIndex)}
                    value={fieldValue}
                />
            );
        }

        // Default: number input
        return (
            <input
                className={`${styles.scratch_input} nodrag my-2`}
                type="number"
                onChange={(event) => handleDataChange(event, inputIndex)}
                value={fieldValue}
            />
        );
    };

    const isUnaryOperator = [16, 17, 18].includes(parseInt(operatorType));
    const isMapOperator = parseInt(operatorType) === 18;

    const OPERATOR_DESCRIPTION_KEYS = {
        '0': 'operations.operators.descriptions.none',
        '1': 'operations.operators.descriptions.add',
        '2': 'operations.operators.descriptions.subtract',
        '3': 'operations.operators.descriptions.multiply',
        '4': 'operations.operators.descriptions.divide',
        '5': 'operations.operators.descriptions.lessThan',
        '6': 'operations.operators.descriptions.greaterThan',
        '7': 'operations.operators.descriptions.lessOrEqual',
        '8': 'operations.operators.descriptions.greaterOrEqual',
        '9': 'operations.operators.descriptions.equal',
        '10': 'operations.operators.descriptions.notEqual',
        '14': 'operations.operators.descriptions.delay',
        '15': 'operations.operators.descriptions.timer',
        '16': 'operations.operators.descriptions.not',
        '17': 'operations.operators.descriptions.delta',
        '18': 'operations.operators.descriptions.map',
    };
    const descriptionKey = OPERATOR_DESCRIPTION_KEYS[String(operatorType)];

    return (
        <div className={`${styles.scratch_block_operation} shadow`}>
            <Handle type="target" position={Position.Top} id={"i1" + id} style={{left: isUnaryOperator ? 110 : 70}}/>
            {!isUnaryOperator && <Handle type="target" position={Position.Top} id={"i2" + id} style={{left: 150}}/>}
            <div className="d-flex flex-row w-100 justify-content-between">
                <div className={'d-flex'}>
                    <div className={styles.scratch_block_type_icon_operation}>
                        <FontAwesomeIcon icon={faGear} color="white"/>
                    </div>
                    <div className={styles.scratch_block_type}>{t('operations.nodes.operation')}</div>
                </div>
                <div className={'px-2 pointer-event'} onClick={() => {
                    if (typeof data.onDeleteNode === 'function') {
                        data.onDeleteNode(id);
                    } else {
                        if (typeof data.setNodes === 'function') {
                            data.setNodes((prev) => [...prev.filter((node) => node.id !== id)]);
                        }
                        if (typeof data.setDeletionList === 'function') {
                            data.setDeletionList((prev) => [...prev, id]);
                        }
                    }
                }}>
                    <FontAwesomeIcon icon={faX} color={'#999'} size={'sm'}/>
                </div>
            </div>

            {renderInput(1)}

            {typesIncompatible ? (
                <div className={styles.nodeValidationWarning}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>{t('operations.nodes.incompatibleTypes')}</span>
                </div>
            ) : (
                <div style={{display:'flex', alignItems:'center', gap:'4px'}}>
                    <select className={`${styles.scratch_select} nodrag`} onChange={(event) => {handleOperatorChange(event)}} value={operatorType}>
                        {filteredOperators.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                    </select>
                    {descriptionKey && (
                        <div className={styles.operatorInfoIcon}>
                            <FontAwesomeIcon icon={faCircleInfo} size="xs" />
                            <span className={styles.operatorInfoTooltipText}>{t(descriptionKey)}</span>
                        </div>
                    )}
                </div>
            )}
            {isMapOperator ? (
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px', marginTop:'6px'}}>
                    <input className={`${styles.scratch_input} nodrag`} type="number" placeholder="From Low"
                        value={mapFromLow} onChange={handleMapParamChange(setMapFromLow, 'map_from_low')} />
                    <input className={`${styles.scratch_input} nodrag`} type="number" placeholder="From High"
                        value={mapFromHigh} onChange={handleMapParamChange(setMapFromHigh, 'map_from_high')} />
                    <input className={`${styles.scratch_input} nodrag`} type="number" placeholder="To Low"
                        value={mapToLow} onChange={handleMapParamChange(setMapToLow, 'map_to_low')} />
                    <input className={`${styles.scratch_input} nodrag`} type="number" placeholder="To High"
                        value={mapToHigh} onChange={handleMapParamChange(setMapToHigh, 'map_to_high')} />
                </div>
            ) : (!isUnaryOperator && renderInput(2))}
            <div className="w-100 mt-2" style={{borderTop: '1px solid #D3ADF7', paddingTop: '8px'}}>
                <div style={{fontSize: '12px', color: '#722ED1', fontWeight: 'bold', marginBottom: '4px'}}>{t('operations.nodes.saveAsVariable')}</div>
                <input
                    className={`${styles.scratch_input} nodrag`}
                    placeholder={t('operations.nodes.variableNameOptional')}
                    onChange={handleResultNameChange}
                    value={resultName}
                    style={{fontSize: '12px', padding: '6px 8px'}}
                />
            </div>

            {/* Operator type indicator */}
            <div className={isComparison ? styles.operatorTypeComparison : styles.operatorTypeArithmetic}>
                <FontAwesomeIcon icon={isComparison ? faCodeCompare : faCalculator} size="xs" />
                <span>{isComparison ? t('operations.nodes.comparisonType') : t('operations.nodes.arithmeticType')}</span>
                {isArithmetic && (
                    <div className={styles.operatorTooltip}>
                        <span className={styles.operatorTooltipText}>
                            {t('operations.nodes.arithmeticCannotTrigger')}
                        </span>
                    </div>
                )}
            </div>

            <Handle type="source" position={Position.Bottom} id="out" />
        </div>
    );
}
